import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from '../../components/common/PaymentForm';
import { XMarkIcon, ShieldCheckIcon, CheckIcon } from '@heroicons/react/24/outline';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const SPECIALIZED_AGENTS = {
  revenue: {
    name: 'Revenue Radar',
    price: 99,
    priceId: 'price_1QoczsJ0cEYz9u0GBQQIkFAW',
    description: 'AI-powered revenue optimization',
    features: [
      'Real-time pricing optimization',
      'Advanced lead scoring',
      'Churn prediction & prevention'
    ]
  },
  market: {
    name: 'Market Alchemist',
    price: 149,
    priceId: 'price_1Qod0PJ0cEYz9u0GUeIB5RGc',
    description: 'Market intelligence and strategy',
    features: [
      'Competitor movement tracking',
      'Market opportunity mapping',
      'Brand sentiment analysis'
    ]
  },
  ops: {
    name: 'Ops Sentinel',
    price: 129,
    priceId: 'price_1Qod0mJ0cEYz9u0GBcXjkW8t',
    description: 'Operations optimization',
    features: [
      'Inventory optimization AI',
      'Smart logistics routing',
      'Process efficiency analysis'
    ]
  }
};

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { priceId, addons = [], refreshUser } = location.state || {};
  const [selectedAddons, setSelectedAddons] = useState(addons);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const baseProduct = {
    name: 'Growth Catalyst Platform',
    price: 79,
    description: 'Your AI-powered COO that works 24/7',
    features: [
      'Continuous business process learning & memory',
      'Daily executive briefings',
      '24/7 anomaly detection',
      'Basic workflow automation (50 actions/day)',
      '10GB encrypted knowledge storage'
    ],
    priceId: 'price_xxxxx'
  };

  const handleBack = () => navigate(-1);
  const handleSuccess = () => navigate('/dashboard');

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== SPECIALIZED_AGENTS[addonId].priceId)
        : [...prev, SPECIALIZED_AGENTS[addonId].priceId]
    );
  };

  const findAgentByPriceId = (priceId) => {
    return Object.values(SPECIALIZED_AGENTS).find(agent => agent.priceId === priceId);
  };

  const totalPrice = baseProduct.price + 
    selectedAddons.reduce((sum, priceId) => {
      const agent = findAgentByPriceId(priceId);
      return sum + (agent?.price || 0);
    }, 0);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleBack} />
      <div className="relative bg-[#2a2a2a]/90 backdrop-blur-sm rounded-lg shadow-lg border border-white/[0.08] w-[1000px] max-h-[85vh] overflow-y-auto">
        <div className="p-8">
          <button 
            onClick={handleBack}
            className="absolute top-6 right-6 text-gray-400 hover:text-black"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-2 mb-8">
            <ShieldCheckIcon className="w-6 h-6 text-blue-500" />
            <h2 className="text-2xl font-medium text-gray-200">Secure Checkout</h2>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Left Column - Order Details */}
            <div>
              {/* Base Platform */}
              <div className="bg-[#1e1e1e]/80 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-medium text-gray-200 mb-2">{baseProduct.name}</h3>
                <p className="text-gray-400 mb-4">{baseProduct.description}</p>
                <ul className="space-y-2 mb-4">
                  {baseProduct.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                      <span className="text-blue-400 mr-2">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-2xl font-bold">${baseProduct.price}/month</div>
              </div>

              {/* Specialized Agents */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-200">Add Specialized Agents</h3>
                {Object.entries(SPECIALIZED_AGENTS).map(([id, agent]) => (
                  <div 
                    key={id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedAddons.includes(SPECIALIZED_AGENTS[id].priceId)
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/[0.08] hover:border-white/20'
                    }`}
                    onClick={() => toggleAddon(id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-gray-200 font-medium">{agent.name}</h4>
                        <p className="text-sm text-gray-400 mb-2">{agent.description}</p>
                        <ul className="space-y-1">
                          {agent.features.map((feature, index) => (
                            <li key={index} className="text-sm text-gray-300 flex items-center">
                              <span className="text-blue-400 mr-2">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-gray-200">+${agent.price}/mo</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Payment */}
            <div>
              {/* Order Summary */}
              <div className="bg-[#1e1e1e]/80 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-medium text-gray-200 mb-4">Order Summary</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Base Platform</span>
                    <span className="text-gray-200">${baseProduct.price}/mo</span>
                  </div>
                  {selectedAddons.map(priceId => {
                    const agent = findAgentByPriceId(priceId);
                    if (!agent) return null;
                    return (
                      <div key={priceId} className="flex justify-between">
                        <span className="text-gray-400">{agent.name}</span>
                        <span className="text-gray-200">+${agent.price}/mo</span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-white/[0.08] pt-4">
                  <div className="flex justify-between text-xl font-medium">
                    <span className="text-gray-200">Total</span>
                    <span className="text-gray-200">${totalPrice}/mo</span>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="bg-[#1e1e1e]/80 rounded-lg p-6 mb-6">
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    priceId={priceId}
                    addons={selectedAddons}
                    onSuccess={handleSuccess}
                    onError={setError}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    refreshUser={refreshUser}
                  />
                </Elements>
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-400">
                    I agree to the <a href="/terms" className="text-blue-400 hover:text-blue-300">Terms of Service</a> and 
                    acknowledge that my subscription will automatically renew monthly. I can cancel anytime.
                  </span>
                </label>

                {error && (
                  <div className="bg-red-500/10 border border-red-500 rounded p-3">
                    <p className="text-red-500 text-sm">{error}</p>
                  </div>
                )}

                <div className="text-sm text-gray-400">
                  <ShieldCheckIcon className="w-4 h-4 inline mr-2 text-green-400" />
                  Your payment is secured with bank-grade encryption
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage; 